"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useMemo, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Calendar, MapPin, Clock, Lightbulb, Users, Brain, Network, Scale } from "lucide-react";
const POLY_GREEN = "#154734";
const MUSTANG_GOLD = "#BD8B13";

//  Put your image in /public (same filename) or update this path to wherever it lives in your app
const HERO_IMAGE_SRC = "/20201204-EngineeringIV-JoeJ0056-1024x634.jpg";
const EVENT_START = new Date("2026-03-14T09:00:00");
const DAYS_RING_START = new Date("2026-01-05T00:00:00");
const SEGMENTS = 60;
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}
function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}
function msBetween(a: Date, b: Date) {
  return b.getTime() - a.getTime();
}
function useCountdown(targetDate: Date) {
  const [msLeft, setMsLeft] = useState(() => Math.max(0, targetDate.getTime() - Date.now()));
  useEffect(() => {
    const t = setInterval(() => {
      setMsLeft(Math.max(0, targetDate.getTime() - Date.now()));
    }, 250);
    return () => clearInterval(t);
  }, [targetDate]);
  const totalSeconds = Math.floor(msLeft / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor(totalSeconds % 86400 / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  return {
    days,
    hours,
    minutes,
    seconds,
    msLeft
  };
}
function polarPoint(cx: number, cy: number, r: number, angleDeg: number) {
  const a = angleDeg * Math.PI / 180;
  return {
    x: cx + r * Math.cos(a),
    y: cy + r * Math.sin(a)
  };
}
interface CountdownRingProps {
  label: string;
  value: string;
  progress: number;
}
function CountdownRing({
  label,
  value,
  progress
}: CountdownRingProps) {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 75;
  const innerR = 62;
  const strokeWidth = 7;
  const activeCount = Math.round(clamp01(progress) * SEGMENTS);
  return <div className="flex flex-col items-center gap-4 group">
      <div className="relative grid place-items-center transition-transform duration-500 hover:scale-110" style={{
      width: size,
      height: size
    }}>
        {/* Glow effect behind ring */}
        <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" style={{
        background: `radial-gradient(circle, ${MUSTANG_GOLD}60 0%, transparent 70%)`
      }} />

        <svg width={size} height={size} className="block relative z-10" style={{
        background: "transparent"
      }} aria-label={`${label} ring`}>
          {/* Background ring */}
          <circle cx={cx} cy={cy} r={(outerR + innerR) / 2} fill="none" stroke="rgba(255, 255, 255, 0.15)" strokeWidth={strokeWidth + 2} />

          {/* Animated segments */}
          {Array.from({
          length: SEGMENTS
        }).map((_, i) => {
          const angle = -90 + 360 / SEGMENTS * i;
          const p1 = polarPoint(cx, cy, innerR, angle);
          const p2 = polarPoint(cx, cy, outerR, angle);
          const isActive = i < activeCount;
          return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={isActive ? MUSTANG_GOLD : "rgba(255, 255, 255, 0.2)"} strokeWidth={strokeWidth} strokeLinecap="round" className="transition-all duration-300" style={{
            filter: isActive ? `drop-shadow(0 0 6px ${MUSTANG_GOLD})` : "none"
          }} />;
        })}
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <div className="text-6xl font-bold tracking-tight" style={{
          color: "#FFFFFF",
          textShadow: `0 0 20px ${MUSTANG_GOLD}80`
        }}>
            {value}
          </div>
          <div className="mt-3 text-xs font-semibold tracking-[0.2em]" style={{
          color: MUSTANG_GOLD,
          textTransform: "uppercase"
        }}>
            {label}
          </div>
        </div>
      </div>
    </div>;
}
const eventTopics = [{
  icon: Brain,
  title: "Large Language Models (LLMs)",
  description: "Explore the latest advancements in LLM technology and applications"
}, {
  icon: Lightbulb,
  title: "Machine Learning Applications in Healthcare",
  description: "Discover practical ML implementations transforming patient care"
}, {
  icon: Users,
  title: "Visual and Perceptual AI",
  description: "Learn about computer vision and diagnostic imaging innovations"
}, {
  icon: Network,
  title: "Systems Thinking and AI in Healthcare",
  description: "Understanding holistic approaches to healthcare transformation"
}, {
  icon: Scale,
  title: "Risk Governance and Ethical Considerations",
  description: "Navigate the ethical landscape of AI in medicine"
}];
export default function HeroHeader() {
  const [forcedVisible, setForcedVisible] = useState(false);
  const {
    ref: logoRef,
    isVisible: logoVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.05,
    rootMargin: "0px 0px -50px 0px"
  });
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.05,
    rootMargin: "0px 0px -50px 0px"
  });
  useEffect(() => {
    const timer = setTimeout(() => setForcedVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const {
    days,
    hours,
    minutes,
    seconds,
    msLeft
  } = useCountdown(EVENT_START);
  const ringProgress = useMemo(() => {
    const totalWindowMs = Math.max(1, msBetween(DAYS_RING_START, EVENT_START));
    const elapsedMs = clamp01(msBetween(DAYS_RING_START, new Date()) / totalWindowMs);
    const hourP = clamp01(hours / 24);
    const minP = clamp01(minutes / 60);
    const secP = clamp01(seconds / 60);
    if (msLeft <= 0) {
      return {
        dayP: 1,
        hourP: 0,
        minP: 0,
        secP: 0
      };
    }
    return {
      dayP: elapsedMs,
      hourP,
      minP,
      secP
    };
  }, [hours, minutes, seconds, msLeft]);
  const showLogo = logoVisible || forcedVisible;
  const showTitle = titleVisible || forcedVisible;
  return <section id="hero-header" className="relative w-full overflow-hidden" style={{
    backgroundColor: POLY_GREEN
  }}>
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-8 md:px-16 relative z-10 py-20 md:py-32">
        <div className="flex flex-col items-center space-y-16">
          {/* Logo Section */}
          <div ref={logoRef} className={`transition-all duration-[var(--duration-slow)] ${showLogo ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}></div>

          {/* Title Section */}
          <div ref={titleRef} className={`transition-all duration-[var(--duration-slow)] delay-100 ${showTitle ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"} text-center space-y-8`}>
            <h1 className="font-serif font-bold leading-tight tracking-tight">
              <span className="block text-white text-3xl md:text-4xl mb-4">The Emerging Leaders Conference:</span>
              <span className="block text-5xl md:text-7xl lg:text-8xl" style={{
              color: MUSTANG_GOLD
            }}>
                AI in Healthcare
              </span>
            </h1>

            {/* Event Details */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-white text-base md:text-lg pt-4">
              <div className="flex items-center gap-2">
                <SafeHydrate><Calendar className="w-5 h-5" style={{
                  color: MUSTANG_GOLD
                }} /></SafeHydrate>
                <span>March 14-15, 2026</span>
              </div>
              <div className="hidden md:block w-px h-6 bg-white/30" />
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" style={{
                color: MUSTANG_GOLD
              }} />
                <span>Daily Programming: 9 AM - 5 PM</span>
              </div>
              <div className="hidden md:block w-px h-6 bg-white/30" />
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{
                color: MUSTANG_GOLD
              }} />
                <span>Cal Poly - San Luis Obispo</span>
              </div>
            </div>
          </div>

          {/* Countdown Section */}
          <div className="w-full max-w-6xl py-8">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 lg:gap-16">
              <CountdownRing label="Days" value={pad2(days)} progress={ringProgress.dayP} />
              <CountdownRing label="Hours" value={pad2(hours)} progress={ringProgress.hourP} />
              <CountdownRing label="Minutes" value={pad2(minutes)} progress={ringProgress.minP} />
              <CountdownRing label="Seconds" value={pad2(seconds)} progress={ringProgress.secP} />
            </div>
          </div>

          {/* ✅ NEW: Image Block (rounded corners + cool animation) */}
          <div className={`w-full max-w-6xl transition-all duration-[var(--duration-slow)] delay-200 ${showTitle ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="group relative rounded-3xl overflow-hidden border border-white/15 shadow-2xl bg-white/5 backdrop-blur-sm animate-float-slow">
              {/* Soft gold glow behind */}
              <div className="absolute -inset-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-3xl" style={{
              background: `radial-gradient(circle at 30% 20%, ${MUSTANG_GOLD}55, transparent 60%)`
            }} />

              {/* Shine sweep */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -left-1/3 top-0 h-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine-sweep" />
              </div>

              {/* Image */}
              <img src="https://eilhconference.org/wp-content/uploads/2026/02/EngineeringIV-V2.jpg" alt="Cal Poly campus" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] group-hover:rotate-[-0.25deg]" />

              {/* Bottom gradient + caption */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                  <div>
                    <div className="text-white font-semibold text-lg md:text-xl">Hosted at Engineering IV — Cal Poly SLO</div>
                    <div className="text-white/80 text-sm md:text-base">
                      A place for builders, researchers, and future healthcare leaders.
                    </div>
                  </div>
                  
                </div>
              </div>

              {/* Subtle inner border */}
              <div className="absolute inset-0 rounded-3xl ring-1 ring-white/10 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Event Topics Section - Enhanced Design */}
      <div className="relative border-t-4 border-white/20 py-20 px-8 md:px-16 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-2" style={{
        background: `linear-gradient(90deg, transparent, ${MUSTANG_GOLD}, transparent)`
      }} />

        {/* Animated gradient orbs */}
        <div className="absolute top-10 left-10 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse-slow" style={{
        backgroundColor: MUSTANG_GOLD
      }} />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse-slow" style={{
        backgroundColor: MUSTANG_GOLD,
        animationDelay: "2s"
      }} />

        <div className="container mx-auto max-w-7xl relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block">
              <h2 className="text-5xl md:text-6xl font-bold mb-4 relative" style={{
              color: MUSTANG_GOLD
            }}>
                EVENT TOPICS
                <div className="absolute -bottom-2 left-0 right-0 h-1 rounded-full" style={{
                backgroundColor: MUSTANG_GOLD
              }} />
              </h2>
            </div>
            <p className="text-white/80 text-xl mt-8 max-w-3xl mx-auto">
              Featuring opportunities for AI experts, engineers, and medical students to engage in discussions, workshops, and professional networking.
            </p>
          </div>

          {/* Topics Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventTopics.map((topic, index) => {
            const Icon = topic.icon;
            return <div key={index} className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/10 hover:border-white/30 transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden" style={{
              transitionDelay: `${index * 100}ms`
            }}>
                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                background: `radial-gradient(circle at top right, ${MUSTANG_GOLD}20, transparent 70%)`
              }} />

                  {/* Icon */}
                  <div className="relative z-10 mb-6">
                    <div className="inline-flex p-4 rounded-xl transition-all duration-300 group-hover:scale-110" style={{
                  backgroundColor: `${MUSTANG_GOLD}20`,
                  boxShadow: `0 0 20px ${MUSTANG_GOLD}40`
                }}>
                      <Icon className="w-8 h-8" style={{
                    color: MUSTANG_GOLD
                  }} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold mb-3 group-hover:text-shadow-lg transition-all" style={{
                  color: MUSTANG_GOLD
                }}>
                      {topic.title}
                    </h3>
                    <p className="text-white/80 text-base leading-relaxed">{topic.description}</p>
                  </div>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500 rounded-full" style={{
                backgroundColor: MUSTANG_GOLD
              }} />
                </div>;
          })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <p className="text-white text-lg md:text-xl leading-relaxed max-w-4xl mx-auto">
              Join us for the{" "}
              <span className="font-bold" style={{
              color: MUSTANG_GOLD
            }}>
                first annual conference
              </span>{" "}
              designed to equip the next generation of healthcare leaders with the core AI concepts and implications needed to make informed decisions.
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.4;
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        /*  NEW: subtle float */
        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        /*  NEW: shine sweep across image */
        @keyframes shine-sweep {
          0% {
            transform: translateX(-120%);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          50% {
            opacity: 0.9;
          }
          100% {
            transform: translateX(220%);
            opacity: 0;
          }
        }
        .animate-shine-sweep {
          animation: shine-sweep 4.8s ease-in-out infinite;
        }
      `}</style>
    </section>;
}